import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";

import { SelectChangeEvent } from "@mui/material";
import {
  AvailablePaymentCurrency,
  FeatureFlagValue,
  formatCurrencyFromCents,
  getDollarValue,
  isRepeatDonorTipExperimentName,
} from "@simplyk/common";
import { useSearchParams } from "next/navigation";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import {
  getForcedRepeatDonorVariant,
  REPEAT_DONOR_EXPERIMENT_QUERY_PARAM,
} from "@/common/constants/repeat-donor-experiment";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

export type RepeatDonorTipVariant =
  | FeatureFlagValue.C
  | FeatureFlagValue.D
  | FeatureFlagValue.E
  | FeatureFlagValue.F
  | FeatureFlagValue.G;

const REPEAT_DONOR_TIP_VARIANTS: RepeatDonorTipVariant[] = [
  FeatureFlagValue.C,
  FeatureFlagValue.D,
  FeatureFlagValue.E,
  FeatureFlagValue.F,
  FeatureFlagValue.G,
];

// The redesigned experiences always start at their own tip percentage, even when the fetched suggestions differ (QA override).
export const REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE = 5;

export const REPEAT_DONOR_TIP_PERCENTAGE_BY_VARIANT: Record<RepeatDonorTipVariant, number> = {
  [FeatureFlagValue.C]: REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE,
  [FeatureFlagValue.D]: REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE,
  [FeatureFlagValue.E]: REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE,
  [FeatureFlagValue.F]: REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE,
  [FeatureFlagValue.G]: 3,
};

export const useRepeatDonorTipVariant = (): RepeatDonorTipVariant | null => {
  const { tipExperiment, displayTipSelection, suggestedTips } = useContext(FrontendFormContext);
  const searchParams = useSearchParams();
  const forcedVariant = getForcedRepeatDonorVariant(searchParams?.get(REPEAT_DONOR_EXPERIMENT_QUERY_PARAM));

  if (!displayTipSelection || !suggestedTips?.length) {
    return null;
  }
  if (forcedVariant) {
    const forcedFeatureFlagValue = forcedVariant.toUpperCase() as FeatureFlagValue;
    return REPEAT_DONOR_TIP_VARIANTS.find((variant) => variant === forcedFeatureFlagValue) ?? null;
  }
  if (!tipExperiment || !isRepeatDonorTipExperimentName(tipExperiment.name)) {
    return null;
  }
  return REPEAT_DONOR_TIP_VARIANTS.find((variant) => variant === tipExperiment.value) ?? null;
};

export const useRepeatDonorTipLine = (variant: RepeatDonorTipVariant | null) => {
  const {
    category,
    currency = AvailablePaymentCurrency.Usd,
    displayedFormAmount,
    formData,
    freeTips,
    getTipChoice,
    handleChangeFreeTips,
    handleChangeTips,
    logAmplitudeTipsChanged,
    organization,
    suggestedTips,
    tip,
    tipBaseAmount,
    updateFreeTipAmount,
  } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const [isEditingTip, setIsEditingTip] = useState(false);
  const [hasEditedTip, setHasEditedTip] = useState(false);

  const defaultTipPercentage = variant
    ? REPEAT_DONOR_TIP_PERCENTAGE_BY_VARIANT[variant]
    : REPEAT_DONOR_DEFAULT_TIP_PERCENTAGE;
  const defaultTipSuggestion = suggestedTips?.find((suggestion) => suggestion.percentage === defaultTipPercentage);
  const defaultTipCents = defaultTipSuggestion?.amount ?? Math.round((tipBaseAmount * defaultTipPercentage) / 100);

  const logTipChanged = (value: string, tipAmountInCents: number) => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormTipChanged, {
      amount: getDollarValue(displayedFormAmount),
      form_id: formData.id,
      form_type: category,
      organization_country: organization.country,
      tip_choice: getTipChoice(value),
      tip_amount: getDollarValue(tipAmountInCents),
    });
  };

  const applyDefaultTip = () => {
    if (defaultTipSuggestion) {
      handleChangeTips({ target: { value: defaultTipSuggestion.percentage } } as SelectChangeEvent<unknown>);
    } else {
      handleChangeTips({ target: { value: "free" } } as SelectChangeEvent<unknown>);
      updateFreeTipAmount(getDollarValue(defaultTipCents));
    }
  };

  // Force the initial tip to the variant's percentage when the fetched suggestions don't contain it (QA override),
  // so the displayed percentage and the charged amount always match.
  const forcedTipAmountRef = useRef<number | null>(null);
  useEffect(() => {
    if (!variant || !displayedFormAmount || defaultTipSuggestion) {
      return;
    }
    if (forcedTipAmountRef.current === displayedFormAmount) {
      return;
    }
    forcedTipAmountRef.current = displayedFormAmount;
    handleChangeTips({ target: { value: "free" } } as SelectChangeEvent<unknown>);
    updateFreeTipAmount(getDollarValue(Math.round((tipBaseAmount * defaultTipPercentage) / 100)));
  }, [
    variant,
    displayedFormAmount,
    tipBaseAmount,
    defaultTipSuggestion,
    defaultTipPercentage,
    handleChangeTips,
    updateFreeTipAmount,
  ]);

  const toggleTip = (checked: boolean) => {
    if (checked) {
      applyDefaultTip();
      logTipChanged(
        defaultTipSuggestion ? String(defaultTipSuggestion.percentage) : "free",
        defaultTipSuggestion?.amount ?? defaultTipCents
      );
    } else {
      handleChangeTips({ target: { value: "free" } } as SelectChangeEvent<unknown>);
      updateFreeTipAmount(0);
      logTipChanged("free", 0);
    }
  };

  const startEditingTip = () => {
    setIsEditingTip(true);
    handleChangeTips({ target: { value: "free" } } as SelectChangeEvent<unknown>);
    updateFreeTipAmount(getDollarValue(tip));
    logTipChanged("free", tip);
  };

  const editTipAmount = (cents: number | null) => {
    setHasEditedTip(true);
    if (cents === null) {
      handleChangeFreeTips({ target: { value: "" } } as ChangeEvent<HTMLInputElement>);
    } else {
      updateFreeTipAmount(getDollarValue(cents));
    }
  };

  const blurTip = () => {
    logAmplitudeTipsChanged({
      target: { value: freeTips === "" ? "" : String(freeTips) },
    } as ChangeEvent<HTMLInputElement>);
  };

  return {
    blurTip,
    currency,
    defaultTipPercentage,
    editedTipCents: typeof freeTips === "number" ? Math.round(freeTips * 100) : null,
    editTipAmount,
    formattedAmountWithoutTip: formatCurrencyFromCents(displayedFormAmount, {
      isoLocale,
      currency,
      cents: "hiddenIfZero",
    }),
    hasEditedTip,
    isEditingTip,
    isTipChecked: tip > 0,
    organizationName: organization.name,
    startEditingTip,
    tip,
    toggleTip,
  };
};
